import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import IconProtectionPlan from 'components/icons/iconProtectionPlan';
import IconPetProtector from 'components/icons/iconPetProtector';
import ProgressLoaderDots from 'components/essentials/ProgressLoaderDots/ProgressLoaderDots';
import Currency from 'components/consumer/Currency';
import CtaLink from 'components/essentials/Cta/CtaLink';
import LoginSidebar from 'components/consumer/LoginSidebar/LoginSidebar';
import { Redirect } from 'react-router-dom';
import ProtectionPlanModal from 'components/consumer/ProtectionPlanModal';
import useUserData from 'data-hooks/useUserData';

const ProtectionPlanButtonAndModal = ({
  close,
  eligible_plans,
  forPage,
  isAddPPLoading,
  isOpen,
  isRemovePPError,
  isRemovePPLoading,
  onSelect,
  openModal,
  productId,
  removeProtectionPlan,
  selectedProtectionPlan,
  showInactiveProtectionPlan,
  showSignInPageUponSelection,
}) => {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { isLoggedIn } = useUserData();
  const buttonRef = useRef(null);

  const accessibleLabel = selectedProtectionPlan
    ? `Remove ${selectedProtectionPlan.plan_name}`
    : `Add 5-year Protection Plan`;

  return (
    <>
      <ProtectionPlanModal
        buttonRef={buttonRef}
        close={close}
        eligiblePlans={eligible_plans}
        forPage={forPage}
        isEdit
        isOpen={isOpen}
        onSelect={onSelect}
        selectedPlan={selectedProtectionPlan}
        showInactiveProtectionPlan={showInactiveProtectionPlan}
      />
      {!isLoggedIn && showSignInPageUponSelection && showSignInModal ? (
        <LoginSidebar
          show={showSignInModal}
          onClose={() => setShowSignInModal(false)}
        />
      ) : null}
      {isLoggedIn && showSignInPageUponSelection && (
        <Redirect to="/customer/orders" />
      )}

      <div className="mt-5">
        {' '}
        {isRemovePPError && (
          <div className="text-sm font-bold text-red">{`${isRemovePPError}`}</div>
        )}
        <button
          className={cx(
            'flex relative items-center mt-0 min-h-[3rem] cursor-pointer border border-solid border-gray-light6 rounded p-3 bg-transparent text-left w-full',
            'hover:border hover:border-solid hover:border-gray-light2 hover:cursor-pointer',
            {
              active: !!selectedProtectionPlan,
            }
          )}
          type="button"
          aria-label={accessibleLabel}
          title={accessibleLabel}
          ref={buttonRef}
          onClick={() => {
            if (showSignInPageUponSelection) {
              setShowSignInModal(true);
              return;
            }
            if (selectedProtectionPlan && removeProtectionPlan) {
              // already there is protection plan , then when clicked go to remove
              removeProtectionPlan(true);
            } else {
              openModal(productId);
            }
          }}
        >
          {isAddPPLoading || isRemovePPLoading ? (
            <ProgressLoaderDots />
          ) : (
            <>
              {selectedProtectionPlan ? (
                <div className="w-12 h-12 mr-1">
                  {selectedProtectionPlan.plan_name.includes(
                    'Pet Protector Plan'
                  ) ? (
                    <IconPetProtector />
                  ) : (
                    <IconProtectionPlan />
                  )}
                </div>
              ) : (
                <div />
              )}
              <div className="cursor-pointer w-full flex items-center min-w-0">
                {selectedProtectionPlan ? (
                  <span
                    className={`block ml-2 text-sm font-bold text-gray flex justify-between items-center gap-2 w-full min-w-0 ${forPage}`}
                  >
                    <span className="flex-1 min-w-0 break-words">
                      5-year {selectedProtectionPlan.plan_name}
                      {` (+`}
                      <Currency>{selectedProtectionPlan.plan_price}</Currency>
                      {`) `}
                    </span>

                    {removeProtectionPlan ? (
                      <span className="flex-shrink-0 text-xs font-bold text-brand hover:underline">
                        Remove
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-brand hover:underline">
                        <CtaLink
                          to="/furniture-protection-plan/"
                          size="xs"
                          target="_blank"
                          color="brand"
                          hasArrow={false}
                        >
                          See Details
                        </CtaLink>
                      </span>
                    )}
                  </span>
                ) : (
                  <>
                    <span className="w-12 h-12 mr-1">
                      <IconProtectionPlan circleFill="none" fill="#e0e0e0" />{' '}
                    </span>
                    <span className="block ml-2 text-sm font-bold text-gray w-full">
                      <span className="flex whitespace-pre flex-col">
                        <span className="whitespace-normal">
                          {'Add 5-year Protection Plan from'}{' '}
                          <Currency>{eligible_plans?.[0]?.plan_price}</Currency>
                        </span>

                        <span className="text-xs font-bold text-brand">
                          What&apos;s covered?
                        </span>
                      </span>
                      <span className="text-xs font-bold text-brand hover:underline" />
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </button>
      </div>
    </>
  );
};

ProtectionPlanButtonAndModal.propTypes = {
  close: PropTypes.func,
  eligible_plans: PropTypes.arrayOf(
    PropTypes.shape({
      plan_name: PropTypes.string,
      plan_price: PropTypes.number,
    })
  ),
  forPage: PropTypes.oneOf([
    'cart',
    'checkout',
    'PDP',
    'UserDashboardOrdersPage',
    'UserDashboardPP',
  ]),
  isAddPPLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  isRemovePPError: PropTypes.string,
  isRemovePPLoading: PropTypes.bool,
  onSelect: PropTypes.func,
  openModal: PropTypes.func,
  productId: PropTypes.string,
  removeProtectionPlan: PropTypes.func,
  selectedProtectionPlan: PropTypes.shape({
    plan_name: PropTypes.string,
    plan_price: PropTypes.number,
  }),
  showInactiveProtectionPlan: PropTypes.bool,
  showSignInPageUponSelection: PropTypes.bool,
};

export default ProtectionPlanButtonAndModal;
